import { Link } from 'react-router-dom';

interface StaffTaskTileProps {
  icon: React.ReactNode;
  label: string;
  to: string;
  count?: number;
  status?: 'default' | 'pending' | 'overdue' | 'done';
}

const colorConfig = {
  default: {
    card: 'bg-gradient-to-br from-white to-primary-50/30 border-l-4 border-primary-400',
    iconBg: 'bg-gradient-to-br from-primary-100 to-primary-50',
    badge: 'bg-gradient-to-r from-primary-500 to-primary-600',
    shadow: 'shadow-md hover:shadow-xl hover:shadow-primary-500/10',
  },
  pending: {
    card: 'bg-gradient-to-br from-white to-accent-orange/5 border-l-4 border-accent-orange',
    iconBg: 'bg-gradient-to-br from-accent-orange/20 to-accent-orange/10',
    badge: 'bg-accent-orange',
    shadow: 'shadow-md hover:shadow-xl hover:shadow-accent-orange/20',
  },
  overdue: {
    card: 'bg-gradient-to-br from-white to-accent-red/5 border-l-4 border-accent-red',
    iconBg: 'bg-gradient-to-br from-accent-red/20 to-accent-red/10',
    badge: 'bg-accent-red',
    shadow: 'shadow-md hover:shadow-xl hover:shadow-accent-red/20',
  },
  done: {
    card: 'bg-gradient-to-br from-white to-accent-green/5 border-l-4 border-accent-green',
    iconBg: 'bg-gradient-to-br from-accent-green/20 to-accent-green/10',
    badge: 'bg-accent-green',
    shadow: 'shadow-md hover:shadow-xl hover:shadow-accent-green/20',
  },
};

export default function StaffTaskTile({ icon, label, to, count, status = 'default' }: StaffTaskTileProps) {
  const config = colorConfig[status];
  const showBadge = typeof count === 'number' && count > 0;

  return (
    <Link
      to={to}
      className={`group relative flex flex-col items-center justify-center gap-4 p-6 sm:p-8 min-h-[150px] sm:min-h-[190px] rounded-xl ${config.card} ${config.shadow} hover:-translate-y-1 transition-all duration-300 overflow-hidden select-none`}
    >
      {/* Hover shimmer */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none" style={{ background: 'linear-gradient(135deg, rgba(255,255,255,0.15) 0%, transparent 60%)' }} />

      {/* Counter badge */}
      {showBadge && (
        <span className={`absolute top-3 right-3 ${config.badge} text-white text-xs font-bold min-w-[22px] h-[22px] px-1.5 rounded-full flex items-center justify-center leading-none shadow-md`}>
          {count! > 99 ? '99+' : count}
        </span>
      )}

      {/* Icon container */}
      <div className={`w-16 h-16 sm:w-20 sm:h-20 rounded-2xl ${config.iconBg} flex items-center justify-center shadow-sm group-hover:shadow-md transition-shadow duration-300`}>
        {icon}
      </div>

      {/* Label */}
      <span className="text-base sm:text-lg font-bold text-neutral-900 text-center leading-tight">
        {label}
      </span>

      {/* Active pulse dot for overdue */}
      {status === 'overdue' && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-accent-red animate-pulse" />
      )}
    </Link>
  );
}
